import { motion, useReducedMotion } from 'framer-motion';
import { RedesignLayout } from 'root/src/client/v2/layout/features/RedesignLayout/RedesignLayout';
import { REDESIGN_NAV_OFFSET_CLASS } from 'root/src/client/v2/layout/helpers/redesignNavbar';
import { RedesignAuthForm, AuthFormModeSwitch } from 'root/src/client/v2/auth/features/AuthForm/RedesignAuthForm';
import { DareDropLogo } from 'root/src/client/v2/common/components/icons/providerIcons';
import { Card } from 'root/src/client/v2/common/components/ui/card';
import { Toaster } from 'root/src/client/v2/common/components/ui/sonner';
import { cn } from 'root/src/client/v2/common/helpers/cn';
// Marketing half of the split auth layout. Purely decorative, so it is hidden below `lg` where the
// form should own the whole viewport instead of pushing it down.
const AuthBrandPanel = ({ headline, subheadline, highlights }) => (<div className="relative hidden overflow-hidden rounded-3xl p-10 lg:flex lg:flex-col lg:justify-between" style={{ background: 'linear-gradient(160deg, var(--grad-brand-from) 0%, hsl(262 83% 40%) 40%, hsl(200 80% 35%) 70%, var(--grad-accent-from) 100%)' }}>
		<div className="absolute right-8 top-12 h-24 w-24 rounded-full border border-solid border-white/[0.15] opacity-40"/>
		<div className="absolute bottom-28 left-6 h-14 w-14 rotate-12 rounded-xl border border-solid border-white/[0.12] opacity-30"/>
		<div className="absolute right-14 top-1/2 h-8 w-8 rounded-full bg-white/[0.08]"/>
		<div className="absolute bottom-44 right-10 h-px w-28 rotate-45 bg-white/[0.15]"/>

		<div className="relative">
			<div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.15] backdrop-blur-sm">
				<DareDropLogo className="h-6 w-6 text-white"/>
			</div>
			<h2 className="mt-7 max-w-sm font-display text-3xl font-bold leading-tight text-white">{headline}</h2>
			<p className="mt-3 max-w-sm text-sm leading-relaxed text-white/70">{subheadline}</p>
		</div>

		<ul className="relative mt-12 space-y-5">
			{highlights.map(highlight => (<li key={highlight.title} className="flex gap-3">
					<span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.12] text-white backdrop-blur-sm">
						{highlight.icon}
					</span>
					<span>
						<span className="block text-sm font-semibold text-white">{highlight.title}</span>
						<span className="block text-[13px] leading-relaxed text-white/60">{highlight.description}</span>
					</span>
				</li>))}
		</ul>

		<p className="relative mt-12 text-[10px] uppercase tracking-wider text-white/30">Dare to discover</p>
	</div>);
// Full-page shell for /sign-in and /sign-up: a marketing panel next to the shared RedesignAuthForm.
// Content is static and the card enters with one gentle fade / slide (respecting reduced motion), so
// there is no layout shift. The page has no footer (see isRedesignFooterlessRoute), which keeps the
// two halves on one viewport.
export const RedesignAuthPage = ({ mode, onModeChange, headline, subheadline, highlights, newsletterOptIn, footnote, }) => {
    const reducedMotion = useReducedMotion();
    return (<RedesignLayout>
			<div className={cn('flex min-h-screen flex-col', REDESIGN_NAV_OFFSET_CLASS)}>
				<div className="mx-auto grid w-full max-w-6xl flex-1 grid-cols-1 items-stretch gap-10 px-4 py-10 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:py-14">
					<AuthBrandPanel headline={headline} subheadline={subheadline} highlights={highlights}/>

					<div className="flex min-w-0 items-center justify-center">
						<motion.div initial={reducedMotion ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="w-full max-w-md">
							<Card className="p-6 sm:p-8">
								<RedesignAuthForm mode={mode} onModeChange={onModeChange} size="page" providerLayout="stacked" newsletterOptIn={newsletterOptIn} footnote={footnote}/>
							</Card>
							<AuthFormModeSwitch mode={mode} onModeChange={onModeChange} className="mt-5 text-[13px]"/>
						</motion.div>
					</div>
				</div>
			</div>
			<Toaster />
		</RedesignLayout>);
};
